import React from 'react'
import { formatPrice } from '../utils/formatters'
import { parseKey } from '../utils/basketKeys'
import Button from '../../../Components/Button'
import QuantityControl from './QuantityControl'
import useModalAccessibility from '../hooks/useModalAccessibility'

export default function BasketModal({ basket, basketItems, menuItems = [], onClose, onUpdateQuantity, onCheckout }) {
  // Look up base menu items by their (plain) id from both the enriched basket
  // (server) and the current page's list. Composite keys parse to a base id.
  const baseById = {}
  Object.values(basketItems).forEach(item => {
    if (item && item.id && !baseById[item.id]) baseById[item.id] = item
  })
  menuItems.forEach(item => {
    if (!baseById[item.id]) baseById[item.id] = item
  })

  // Build a line per basket key: base item + the chosen add-ons and unit price.
  const items = Object.entries(basket)
    .map(([key, quantity]) => {
      if (!quantity || quantity === 0) return null
      const { menuItemId, addOnIds } = parseKey(key)
      const base = baseById[menuItemId]
      if (!base) return null
      const selectedAddOns = (base.add_ons || []).filter(a => addOnIds.includes(a.id))
      const unitPrice = base.price_pence + selectedAddOns.reduce((s, a) => s + a.price_pence, 0)
      return { key, base, quantity, selectedAddOns, unitPrice }
    })
    .filter(Boolean)

  const total = items.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0)

  const isEmpty = items.length === 0

  const { dialogRef, isVisible, handleClose } = useModalAccessibility({ onClose })

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Your basket"
      ref={dialogRef}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className={`relative bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[80vh] flex flex-col transition-transform duration-300 ease-out ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Your Basket</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none focus:outline-none focus:ring-2 focus:ring-sky-500 rounded"
            aria-label="Close basket"
            data-modal-close
          >
            ✕
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isEmpty ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4" aria-hidden="true">🛒</div>
              <p className="text-gray-600">Your basket is empty</p>
            </div>
          ) : (
            <ul className="space-y-4" aria-label="Basket items">
              {items.map(line => (
                <li key={line.key} className="flex items-center gap-4 pb-4 border-b last:border-0">
                  {line.base.image_url && (
                    <img
                      src={line.base.image_url}
                      alt=""
                      className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                    />
                  )}

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {line.base.name}
                    </h3>
                    {line.selectedAddOns.length > 0 && (
                      <ul className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                        {line.selectedAddOns.map(a => (
                          <li key={a.id}>+ {a.name}</li>
                        ))}
                      </ul>
                    )}
                    <p className="text-sm text-gray-600 mt-0.5">
                      {formatPrice(line.unitPrice)}
                    </p>
                  </div>

                  {/* Quantity Controls */}
                  <QuantityControl
                    quantity={line.quantity}
                    onIncrease={() => onUpdateQuantity(line.key, line.quantity + 1)}
                    onDecrease={() => onUpdateQuantity(line.key, line.quantity - 1)}
                    itemName={line.base.name}
                  />

                  <div className="text-right font-semibold text-gray-900 min-w-[60px]">
                    {formatPrice(line.unitPrice * line.quantity)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {!isEmpty && (
          <div className="px-6 py-4 border-t bg-gray-50">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-semibold text-gray-900">Total</span>
              <span className="text-2xl font-bold text-sky-900">
                {formatPrice(total)}
              </span>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleClose}
                variant="secondary"
                theme="dark"
                size="lg"
                className="flex-1"
              >
                Keep Shopping
              </Button>
              <Button
                onClick={onCheckout}
                variant="primary"
                theme="dark"
                size="lg"
                className="flex-1"
              >
                Checkout
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
